import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import type {
  PhoneVerificationNudgeActionType,
  PhoneVerificationNudgeLeg,
  PhoneVerificationNudgeSurface,
} from 'src/utils/analytics/events';
import { tracker } from 'src/utils/analytics';

// Same `ecomm.phone-identity.*` naming family as the Nudge's own operational killswitch
// (`ecomm.phone-identity.operational`), but a DISTINCT flag key -- this must stay dial-able
// independently of the feature itself, so a telemetry volume problem can be turned off without
// touching the Nudge's own rollout.
export const PHONE_VERIFICATION_NUDGE_ANALYTICS_FLAG = 'ecomm.phone-identity.nudge-analytics.rollout';

type TrackArgs = {
  leg?: PhoneVerificationNudgeLeg;
  errorCode?: string;
};

export type UsePhoneVerificationNudgeAnalyticsOptions = {
  surface: PhoneVerificationNudgeSurface;
  /** Closed over by every call from this hook instance when the caller doesn't pass its own. */
  leg?: PhoneVerificationNudgeLeg;
};

export type UsePhoneVerificationNudgeAnalyticsReturn = {
  track: (action: PhoneVerificationNudgeActionType, args?: TrackArgs) => void;
  trackShown: (args?: TrackArgs) => void;
  trackSuppressed: (errorCode: string) => void;
  trackVerifyClicked: () => void;
  trackCodeSent: () => void;
  trackOtpSubmitted: () => void;
  trackOtpResent: () => void;
  trackChangeNumberSubmitted: () => void;
  trackCompleted: () => void;
  trackDismissed: () => void;
  trackLoginOtherAccount: () => void;
  trackFailed: (errorCode?: string) => void;
};

/**
 * Analytics for the phone-verification Nudge funnel (post-login modal, My Account banner + its
 * OTP host, and the login-intent marker). Gated by its own rollout flag rather than the Nudge's
 * feature flags -- see `PHONE_VERIFICATION_NUDGE_ANALYTICS_FLAG` above.
 *
 * Every call is wrapped in its own `try`/`catch`: the Nudge's existing posture is that no
 * failure is ever user-visible (`use-phone-verification-nudge.ts` swallows everything in one
 * `catch`), and telemetry must not be the thing that breaks that contract. Callers can invoke
 * these unconditionally, from inside or outside an existing try block, without adding their own
 * guard.
 */
export function usePhoneVerificationNudgeAnalytics({
  surface,
  leg: defaultLeg,
}: UsePhoneVerificationNudgeAnalyticsOptions): UsePhoneVerificationNudgeAnalyticsReturn {
  const flags = useFlags();
  const isEnabled = flags[PHONE_VERIFICATION_NUDGE_ANALYTICS_FLAG] ?? false;

  const track = useCallback(
    (action: PhoneVerificationNudgeActionType, args: TrackArgs = {}): void => {
      try {
        if (!isEnabled) {
          return;
        }
        const leg = args.leg ?? defaultLeg;
        tracker.phoneVerificationNudgeAction({
          action,
          surface,
          ...(leg !== undefined && { leg }),
          ...(args.errorCode !== undefined && { errorCode: args.errorCode }),
        });
      } catch {
        // Analytics must never throw into the Nudge's control flow -- see the doc comment above.
      }
    },
    [isEnabled, surface, defaultLeg]
  );

  const trackShown = useCallback((args?: TrackArgs): void => track('shown', args), [track]);
  const trackSuppressed = useCallback((errorCode: string): void => track('suppressed', { errorCode }), [track]);
  const trackVerifyClicked = useCallback((): void => track('verify_clicked'), [track]);
  const trackCodeSent = useCallback((): void => track('code_sent'), [track]);
  const trackOtpSubmitted = useCallback((): void => track('otp_submitted'), [track]);
  const trackOtpResent = useCallback((): void => track('otp_resent'), [track]);
  const trackChangeNumberSubmitted = useCallback((): void => track('change_number_submitted'), [track]);
  const trackCompleted = useCallback((): void => track('completed'), [track]);
  const trackDismissed = useCallback((): void => track('dismissed'), [track]);
  const trackLoginOtherAccount = useCallback((): void => track('login_other_account'), [track]);
  const trackFailed = useCallback((errorCode?: string): void => track('failed', { errorCode }), [track]);

  return {
    track,
    trackShown,
    trackSuppressed,
    trackVerifyClicked,
    trackCodeSent,
    trackOtpSubmitted,
    trackOtpResent,
    trackChangeNumberSubmitted,
    trackCompleted,
    trackDismissed,
    trackLoginOtherAccount,
    trackFailed,
  };
}
